import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { GraduationCap, ArrowRight, Building2, Briefcase, BookOpen, TrendingUp, Users, Lightbulb, ChevronRight, ChevronDown, ChevronUp, Search, Mic, DollarSign, MapPin, Heart, Scale, Stethoscope, Star, CheckCircle2, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { MarketingNav } from '../components/MarketingLayout';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const INDUSTRY_CONFIG = {
  media_entertainment: { icon: Building2, color: '#7c3aed', label: 'Media & Entertainment', bgClass: 'bg-purple-50', textClass: 'text-purple-700', borderClass: 'border-purple-200' },
  education: { icon: BookOpen, color: '#0F392B', label: 'Education', bgClass: 'bg-emerald-50', textClass: 'text-emerald-700', borderClass: 'border-emerald-200' },
  sports_coaching: { icon: Users, color: '#D4AF37', label: 'Sports & Coaching', bgClass: 'bg-amber-50', textClass: 'text-amber-700', borderClass: 'border-amber-200' },
};

function IndustrySection({ industryKey, data, expanded, onToggle }) {
  const config = INDUSTRY_CONFIG[industryKey] || { icon: Briefcase, color: '#0F392B', label: industryKey };
  const Icon = config.icon;
  const [search, setSearch] = useState('');

  const careers = (data.careers || []).filter(c =>
    !search || c.title.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4" data-testid={`industry-${industryKey}`}>
      <button onClick={onToggle}
        className="w-full flex items-center gap-4 p-4 rounded-xl bg-white border border-[#E2E2E2] hover:border-[#0F392B]/20 transition-all text-left">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: config.color + '15' }}>
          <Icon className="h-5 w-5" style={{ color: config.color }} />
        </div>
        <div className="flex-1">
          <h2 className="text-base font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>{data.industry || config.label}</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {careers.length} careers · Source: {data.source}
          </p>
        </div>
        {expanded ? <ChevronUp className="h-5 w-5 text-muted-foreground" /> : <ChevronDown className="h-5 w-5 text-muted-foreground" />}
      </button>

      {expanded && (
        <div className="space-y-4 pl-2">
          {/* Overview & Outlook */}
          {(data.overview || data.outlook) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {data.overview && (
                <Card className="border-[#E2E2E2]">
                  <CardContent className="p-4">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Industry Overview</h3>
                    <p className="text-xs text-muted-foreground leading-relaxed">{data.overview}</p>
                  </CardContent>
                </Card>
              )}
              {data.outlook && (
                <Card className="border-[#E2E2E2]">
                  <CardContent className="p-4">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5">
                      <TrendingUp className="h-3 w-3" /> Industry Outlook
                    </h3>
                    <div className="space-y-1.5">
                      {Object.entries(data.outlook).map(([k, v]) => (
                        <div key={k} className="flex items-start gap-2">
                          <span className="text-[10px] font-medium capitalize w-28 flex-shrink-0" style={{ color: config.color }}>
                            {k.replace(/_/g, ' ')}
                          </span>
                          <span className="text-xs text-muted-foreground">{v}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Lifestyle & Tips */}
          {(data.lifestyle || data.interview_tips || data.job_search_tips) && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {data.lifestyle && (
                <Card className="border-[#E2E2E2]">
                  <CardContent className="p-4">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5">
                      <Heart className="h-3 w-3" /> Lifestyle
                    </h3>
                    {Object.entries(data.lifestyle).map(([k, v]) => (
                      <div key={k} className="mb-2">
                        <span className="text-[10px] font-semibold capitalize block" style={{ color: config.color }}>{k.replace(/_/g, ' ')}</span>
                        <p className="text-xs text-muted-foreground">{v}</p>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
              {data.interview_tips && (
                <Card className="border-[#E2E2E2]">
                  <CardContent className="p-4">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5">
                      <Mic className="h-3 w-3" /> Interview Tips
                    </h3>
                    <div className="space-y-1.5">
                      {data.interview_tips.map((tip, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <span className="text-[10px] font-bold mt-0.5" style={{ color: config.color }}>{i + 1}.</span>
                          <p className="text-xs text-muted-foreground">{tip}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
              {data.job_search_tips && (
                <Card className="border-[#E2E2E2]">
                  <CardContent className="p-4">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5">
                      <Search className="h-3 w-3" /> Job Search Tips
                    </h3>
                    <div className="space-y-1.5">
                      {data.job_search_tips.map((tip, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <span className="text-[10px] font-bold mt-0.5" style={{ color: config.color }}>{i + 1}.</span>
                          <p className="text-xs text-muted-foreground">{tip}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Industry Trends */}
          {data.industry_trends && (
            <Card className="border-[#E2E2E2]">
              <CardContent className="p-4">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Industry Trends</h3>
                <div className="flex flex-wrap gap-2">
                  {data.industry_trends.map((t, i) => (
                    <Badge key={i} variant="outline" className="text-[10px] py-1">{t}</Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Sectors (Media) */}
          {data.sectors && (
            <div className="space-y-3">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Industry Sectors</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {data.sectors.map((sector, i) => (
                  <Card key={i} className="border-[#E2E2E2]">
                    <CardContent className="p-4">
                      <h4 className="text-sm font-semibold mb-1">{sector.name}</h4>
                      <p className="text-xs text-muted-foreground mb-2">{sector.description}</p>
                      {sector.key_employers && (
                        <div className="mb-2">
                          <span className="text-[9px] font-semibold uppercase text-muted-foreground">Key Employers: </span>
                          <span className="text-xs text-muted-foreground">{sector.key_employers.join(', ')}</span>
                        </div>
                      )}
                      {sector.roles && (
                        <div className="flex flex-wrap gap-1">
                          {sector.roles.slice(0, 6).map((r, j) => (
                            <Badge key={j} variant="outline" className="text-[9px]">{r}</Badge>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Careers Table */}
          {careers.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Careers ({careers.length})
                </h3>
                <div className="relative w-48">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                  <Input value={search} onChange={e => setSearch(e.target.value)}
                    placeholder="Filter careers..." className="pl-7 h-7 text-xs border-[#E2E2E2]" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {careers.map((career, i) => (
                  <Card key={i} className="border-[#E2E2E2] hover:border-[#0F392B]/20 transition-colors">
                    <CardContent className="p-3">
                      <h4 className="text-xs font-semibold mb-1">{career.title}</h4>
                      <div className="space-y-0.5">
                        <div className="flex items-center gap-1.5">
                          <BookOpen className="h-2.5 w-2.5 text-muted-foreground" />
                          <span className="text-[10px] text-muted-foreground">{career.education}</span>
                        </div>
                        {career.setting && (
                          <div className="flex items-center gap-1.5">
                            <MapPin className="h-2.5 w-2.5 text-muted-foreground" />
                            <span className="text-[10px] text-muted-foreground">{career.setting}</span>
                          </div>
                        )}
                        {career.salary_range && (
                          <div className="flex items-center gap-1.5">
                            <DollarSign className="h-2.5 w-2.5 text-muted-foreground" />
                            <span className="text-[10px] text-muted-foreground">{career.salary_range}</span>
                          </div>
                        )}
                        {career.growth && (
                          <div className="flex items-center gap-1.5">
                            <TrendingUp className="h-2.5 w-2.5 text-emerald-500" />
                            <span className="text-[10px] text-emerald-600">{career.growth}</span>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function CareerPortalPage() {
  const [industries, setIndustries] = useState({});
  const [gradGuides, setGradGuides] = useState({});
  const [traits, setTraits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [activeGradTab, setActiveGradTab] = useState('law_school');

  useEffect(() => {
    Promise.all([
      fetch(`${API_URL}/api/career-manuals/media_entertainment`).then(r => r.json()),
      fetch(`${API_URL}/api/career-manuals/education`).then(r => r.json()),
      fetch(`${API_URL}/api/career-manuals/sports_coaching`).then(r => r.json()),
      fetch(`${API_URL}/api/grad-school-guides/law_school`).then(r => r.json()),
      fetch(`${API_URL}/api/grad-school-guides/medical_school`).then(r => r.json()),
      fetch(`${API_URL}/api/grad-school-guides/grad_school`).then(r => r.json()),
      fetch(`${API_URL}/api/student-traits`).then(r => r.json()),
    ]).then(([media, edu, sports, law, med, grad, studentTraits]) => {
      setIndustries({ media_entertainment: media, education: edu, sports_coaching: sports });
      setGradGuides({ law_school: law, medical_school: med, grad_school: grad });
      setTraits(studentTraits);
      setExpanded('education');
    }).catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="min-h-screen bg-[#F9F9F7] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#0F392B] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F9F9F7] pt-16" data-testid="career-portal-page">
      {/* Nav */}
      <MarketingNav />

      {/* Hero */}
      <section className="bg-[#0F392B] text-white py-14">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Badge className="bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/30 mb-4 text-xs">Career Guide Library</Badge>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Career Portal
          </h1>
          <p className="text-base text-white/70 max-w-xl mx-auto leading-relaxed mb-6">
            Explore in-depth career guides across industries. Browse roles, education requirements, salary data, interview tips, and industry outlook — all sourced from professional career guides.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/career-insights">
              <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 text-xs h-9">
                <Lightbulb className="h-3.5 w-3.5 mr-1.5" /> AI Career Insights
              </Button>
            </Link>
            <Link to="/career-compare">
              <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 text-xs h-9">
                <Briefcase className="h-3.5 w-3.5 mr-1.5" /> Compare Careers
              </Button>
            </Link>
            <Link to="/college-readiness">
              <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 text-xs h-9">
                <GraduationCap className="h-3.5 w-3.5 mr-1.5" /> Readiness Guide
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="max-w-4xl mx-auto px-4 py-10 space-y-4" data-testid="industries-section">
        <h2 className="text-lg font-semibold uppercase tracking-wide text-muted-foreground" style={{ fontFamily: "'Playfair Display', serif" }}>
          Career Industry Guides
        </h2>
        {Object.entries(industries).map(([key, data]) => (
          <IndustrySection
            key={key}
            industryKey={key}
            data={data}
            expanded={expanded === key}
            onToggle={() => setExpanded(expanded === key ? null : key)}
          />
        ))}
      </section>

      {/* Graduate School Pathways */}
      {Object.keys(gradGuides).length > 0 && (
        <section className="max-w-4xl mx-auto px-4 pb-10 space-y-4" data-testid="grad-guides-section">
          <h2 className="text-lg font-semibold uppercase tracking-wide text-muted-foreground" style={{ fontFamily: "'Playfair Display', serif" }}>
            Graduate School Pathways
          </h2>
          <p className="text-xs text-muted-foreground">Comprehensive application guides sourced from Harvard University's Mignone Center for Career Success</p>

          <div className="bg-white rounded-xl border border-[#E2E2E2] p-1 flex gap-1" data-testid="grad-tabs">
            {[
              { key: 'law_school', label: 'Law School', icon: Scale },
              { key: 'medical_school', label: 'Medical School', icon: Stethoscope },
              { key: 'grad_school', label: 'Graduate School', icon: GraduationCap },
            ].map(tab => (
              <button key={tab.key} onClick={() => setActiveGradTab(tab.key)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-xs font-semibold transition-all ${activeGradTab === tab.key ? 'bg-[#0F392B] text-white shadow-md' : 'text-muted-foreground hover:bg-[#F9F9F7]'}`}
                data-testid={`grad-tab-${tab.key}`}>
                <tab.icon className="h-3.5 w-3.5" /> {tab.label}
              </button>
            ))}
          </div>

          {gradGuides[activeGradTab] && (() => {
            const g = gradGuides[activeGradTab];
            return (
              <div className="space-y-4" data-testid={`grad-content-${activeGradTab}`}>
                {/* Overview */}
                <Card className="border-[#E2E2E2]">
                  <CardContent className="p-5">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl bg-[#0F392B]/10 flex items-center justify-center flex-shrink-0">
                        <BookOpen className="h-5 w-5 text-[#0F392B]" />
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold">{g.title}</h3>
                        <p className="text-[10px] text-muted-foreground mt-0.5">{g.source}</p>
                        <p className="text-xs text-muted-foreground leading-relaxed mt-2">{g.overview}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Key Stats */}
                {g.key_stats && (
                  <Card className="border-[#D4AF37]/20 bg-[#D4AF37]/[0.02]">
                    <CardContent className="p-4">
                      <h3 className="text-xs font-semibold uppercase tracking-wide text-[#D4AF37] mb-3">Key Facts</h3>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {Object.entries(g.key_stats).map(([k, v]) => (
                          <div key={k}>
                            <span className="text-[9px] font-semibold uppercase text-muted-foreground">{k.replace(/_/g, ' ')}</span>
                            <p className="text-xs mt-0.5">{v}</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Degree Types (grad school) */}
                {g.degree_types && (
                  <div className="grid grid-cols-3 gap-3">
                    {Object.entries(g.degree_types).map(([k, dt]) => (
                      <Card key={k} className="border-[#E2E2E2]">
                        <CardContent className="p-4 text-center">
                          <h4 className="text-xs font-semibold capitalize">{k.replace('_', "'s")}</h4>
                          <p className="text-lg font-bold text-[#0F392B] mt-1">{dt.duration}</p>
                          <p className="text-[10px] text-muted-foreground mt-1">{dt.focus}</p>
                          <Badge variant="outline" className="text-[9px] mt-2">{dt.funding}</Badge>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}

                {/* Timeline */}
                {g.timeline && (
                  <Card className="border-[#E2E2E2]">
                    <CardContent className="p-5">
                      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3 flex items-center gap-1.5">
                        <Calendar className="h-3 w-3" /> Application Timeline
                      </h3>
                      <div className="space-y-4">
                        {Object.entries(g.timeline).map(([phase, items], idx) => (
                          <div key={phase} className="flex gap-3">
                            <div className="flex flex-col items-center">
                              <div className="w-3 h-3 rounded-full bg-[#0F392B]" style={{ opacity: 1 - idx * 0.12 }} />
                              {idx < Object.keys(g.timeline).length - 1 && <div className="w-0.5 flex-1 bg-[#E2E2E2] mt-1" />}
                            </div>
                            <div className="flex-1 pb-2">
                              <p className="text-xs font-semibold capitalize">{phase.replace(/_/g, ' ')}</p>
                              <div className="space-y-1 mt-1">
                                {items.map((item, i) => (
                                  <div key={i} className="flex items-start gap-1.5">
                                    <CheckCircle2 className="h-3 w-3 text-emerald-400 mt-0.5 flex-shrink-0" />
                                    <span className="text-[11px] text-muted-foreground">{item}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* What Schools Look For + Essay Tips */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {g.what_schools_look_for && (
                    <Card className="border-[#E2E2E2]">
                      <CardContent className="p-4">
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">What Schools Look For</h3>
                        <div className="space-y-1.5">
                          {g.what_schools_look_for.map((item, i) => (
                            <div key={i} className="flex items-start gap-2">
                              <Star className="h-3 w-3 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                              <span className="text-xs text-muted-foreground">{item}</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                  {g.essay_tips && (
                    <Card className="border-[#E2E2E2]">
                      <CardContent className="p-4">
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Essay Tips</h3>
                        <div className="space-y-1.5">
                          {g.essay_tips.map((tip, i) => (
                            <div key={i} className="flex items-start gap-2">
                              <span className="text-[10px] font-bold text-[#0F392B] mt-0.5">{i + 1}.</span>
                              <span className="text-xs text-muted-foreground">{tip}</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* Choosing a School / Clinical Experience / Self-Assessment / Recommendation Tips */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {g.choosing_a_school && (
                    <Card className="border-[#E2E2E2]">
                      <CardContent className="p-4">
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Choosing a School</h3>
                        <div className="space-y-1.5">{g.choosing_a_school.map((item, i) => (
                          <div key={i} className="flex items-start gap-2"><CheckCircle2 className="h-3 w-3 text-emerald-400 mt-0.5 flex-shrink-0" /><span className="text-xs text-muted-foreground">{item}</span></div>
                        ))}</div>
                      </CardContent>
                    </Card>
                  )}
                  {g.clinical_experience && (
                    <Card className="border-[#E2E2E2]">
                      <CardContent className="p-4">
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Clinical Experience</h3>
                        <div className="space-y-1.5">{g.clinical_experience.map((item, i) => (
                          <div key={i} className="flex items-start gap-2"><Stethoscope className="h-3 w-3 text-[#0F392B] mt-0.5 flex-shrink-0" /><span className="text-xs text-muted-foreground">{item}</span></div>
                        ))}</div>
                      </CardContent>
                    </Card>
                  )}
                  {g.self_assessment_questions && (
                    <Card className="border-[#E2E2E2]">
                      <CardContent className="p-4">
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Self-Assessment</h3>
                        <div className="space-y-1.5">{g.self_assessment_questions.map((q, i) => (
                          <div key={i} className="flex items-start gap-2"><Lightbulb className="h-3 w-3 text-[#D4AF37] mt-0.5 flex-shrink-0" /><span className="text-xs text-muted-foreground">{q}</span></div>
                        ))}</div>
                      </CardContent>
                    </Card>
                  )}
                  {g.research_checklist && (
                    <Card className="border-[#E2E2E2]">
                      <CardContent className="p-4">
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Research Checklist</h3>
                        <div className="space-y-1.5">{g.research_checklist.map((item, i) => (
                          <div key={i} className="flex items-start gap-2"><Search className="h-3 w-3 text-muted-foreground mt-0.5 flex-shrink-0" /><span className="text-xs text-muted-foreground">{item}</span></div>
                        ))}</div>
                      </CardContent>
                    </Card>
                  )}
                  {g.recommendation_tips && (
                    <Card className="border-[#E2E2E2]">
                      <CardContent className="p-4">
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Recommendation Tips</h3>
                        <div className="space-y-1.5">{g.recommendation_tips.map((tip, i) => (
                          <div key={i} className="flex items-start gap-2"><span className="text-[10px] font-bold text-[#0F392B] mt-0.5">{i + 1}.</span><span className="text-xs text-muted-foreground">{tip}</span></div>
                        ))}</div>
                      </CardContent>
                    </Card>
                  )}
                  {g.statement_of_purpose_tips && (
                    <Card className="border-[#E2E2E2]">
                      <CardContent className="p-4">
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Statement of Purpose</h3>
                        <div className="space-y-1.5">{g.statement_of_purpose_tips.map((tip, i) => (
                          <div key={i} className="flex items-start gap-2"><span className="text-[10px] font-bold text-[#0F392B] mt-0.5">{i + 1}.</span><span className="text-xs text-muted-foreground">{tip}</span></div>
                        ))}</div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>
            );
          })()}
        </section>
      )}

      {/* What Makes a Good Student */}
      {traits && (
        <section className="max-w-4xl mx-auto px-4 pb-10 space-y-4" data-testid="student-traits-section">
          <h2 className="text-lg font-semibold uppercase tracking-wide text-muted-foreground" style={{ fontFamily: "'Playfair Display', serif" }}>
            What Makes a Good Student
          </h2>
          <Card className="border-[#E2E2E2]">
            <CardContent className="p-5">
              <p className="text-[10px] text-muted-foreground mb-1">{traits.source}</p>
              <p className="text-xs text-muted-foreground leading-relaxed">{traits.overview}</p>
            </CardContent>
          </Card>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {traits.traits.map((t, i) => (
              <Card key={i} className="border-[#E2E2E2] hover:border-[#D4AF37]/30 transition-colors">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                      <span className="text-[9px] font-bold text-[#D4AF37]">{i + 1}</span>
                    </div>
                    <h4 className="text-xs font-semibold">{t.trait}</h4>
                  </div>
                  <p className="text-[11px] text-muted-foreground leading-relaxed">{t.description}</p>
                  <div className="mt-2 pt-2 border-t border-[#F2F2F0]">
                    <p className="text-[10px] text-[#0F392B] font-medium">{t.advice}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-4 pb-12">
        <Card className="border-[#0F392B]/20 bg-[#0F392B]/[0.02]">
          <CardContent className="p-8 text-center">
            <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              Know Where You're Headed?
            </h3>
            <p className="text-sm text-muted-foreground mb-4 max-w-md mx-auto">
              Now let us help you get there. Our college application portal organizes everything — essays, activities, deadlines, recommendations — so you can focus on standing out.
            </p>
            <Link to="/sign-up">
              <Button className="bg-[#0F392B] hover:bg-[#0F392B]/90 text-white h-11 px-8 text-sm font-semibold" data-testid="portal-cta-btn">
                Start Your Application <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#E2E2E2] bg-white py-8">
        <div className="max-w-6xl mx-auto px-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <GraduationCap className="h-4 w-4 text-[#0F392B]" strokeWidth={1.5} />
            <span className="text-xs text-muted-foreground">Ivy League Consulting</span>
          </div>
          <p className="text-[10px] text-muted-foreground">Content sourced from Vault Career Guides & Harvard University</p>
        </div>
      </footer>
    </div>
  );
}
